/**
 * PreviewPanel Component
 *
 * Live preview with device size presets and theme controls.
 */
import { useState, useRef, useMemo, useEffect, useCallback } from 'react';
import {
  Smartphone,
  Tablet,
  Monitor,
  Sun,
  Moon,
  RefreshCw,
  ExternalLink,
  Loader2,
  PanelLeftOpen,
  PanelBottomOpen,
  AppWindow,
  Globe,
  Hammer,
  CheckCircle2,
  AlertTriangle,
} from 'lucide-react';
import { useBuildStore } from '@/stores/buildStore';
import { useDeployStore } from '@/stores/deployStore';
import { useProjectStore } from '@/stores/projectStore';
import { useUIStore } from '@/stores/uiStore';
import { resolveBlockworldCmd } from '@/services/piTools/uiActionTool';
import type { BuildStage, DeviceSize, ThemeMode, FileNode } from '@/types';
import { BrowserView } from './BrowserView';
import { MarkdownRenderer } from '@/components/markdown/MarkdownRenderer';
import clsx from 'clsx';

/**
 * MarkdownPreviewPane — 2026-07-14. When the preview file selector points at a
 * `.md` file, render it as formatted markdown (reusing the chat's
 * MarkdownRenderer) instead of pointing the iframe at raw markdown text. The
 * content is fetched from the same public raw-serve route the iframe uses
 * (/api/preview/<id>/<path>), so it works with no build.
 */
function MarkdownPreviewPane({
  projectId,
  path,
  theme,
  onLoaded,
}: {
  projectId: string;
  path: string;
  theme: ThemeMode;
  onLoaded: () => void;
}) {
  const [content, setContent] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setContent(null);
    setError(null);
    const rel = path.replace(/^\/+/, '');
    const url = `/api/preview/${projectId}/${rel
      .split('/')
      .map(encodeURIComponent)
      .join('/')}`;
    fetch(url)
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.text();
      })
      .then((text) => {
        if (cancelled) return;
        setContent(text);
        onLoaded();
      })
      .catch((e) => {
        if (cancelled) return;
        setError(String(e instanceof Error ? e.message : e));
        onLoaded();
      });
    return () => {
      cancelled = true;
    };
  }, [projectId, path, onLoaded]);

  // 2026-08-18: the card is themeable. LIGHT is the default (the renderer's
  // native vibe-* token styles are dark-panel styles — globals.css carries
  // .vibe-md-preview--light overrides for inline-code chips + table headers,
  // which otherwise render near-black on the white card). DARK keeps the
  // native tokens on a dark card.
  const cardClass =
    theme === 'light'
      ? 'w-full max-w-3xl bg-white text-gray-900 rounded-lg shadow-2xl p-8 overflow-auto'
      : 'w-full max-w-3xl bg-vibe-bg-secondary text-vibe-text border border-vibe-border rounded-lg shadow-2xl p-8 overflow-auto';
  return (
    <div data-testid="preview-markdown-pane" className={cardClass}>
      {error ? (
        <div className="text-red-600 text-sm">
          Could not load <span className="font-mono">{path}</span>: {error}
        </div>
      ) : content === null ? (
        <div className="flex items-center justify-center py-12 text-gray-400">
          <Loader2 className="w-6 h-6 animate-spin" />
        </div>
      ) : (
        <MarkdownRenderer
          content={content}
          className={`vibe-md-preview vibe-md-preview--${theme}`}
        />
      )}
    </div>
  );
}

/** E.21.31.2: matches TopBar.tsx stageLabel; centralize if a 3rd consumer
 * shows up. */
function buildStageMessage(stage: BuildStage | null): string {
  switch (stage) {
    case 'pending':
      return 'Starting build...';
    case 'installing':
      return 'Installing dependencies...';
    case 'building':
      return 'Building project...';
    case 'deploying':
      return 'Packaging output...';
    default:
      return 'Building...';
  }
}

const deviceSizes: Record<DeviceSize, { width: number; height: number }> = {
  mobile: { width: 375, height: 667 },
  tablet: { width: 768, height: 1024 },
  desktop: { width: 1280, height: 800 },
};

export function PreviewPanel() {
  const { previewUrl, previewLoading, setPreviewLoading } = useBuildStore();
  const previewProject = useBuildStore((s) => s.previewProject);
  // The REAL build pipeline (POST /api/builds/{id} → poll status), same pair
  // TopBar's Build button uses. previewProject (above) only HEAD-probes for an
  // existing dist/ — it does NOT recompile, so it must NOT back "Rebuild".
  const startBuild = useBuildStore((s) => s.startBuild);
  const pollBuildStatus = useBuildStore((s) => s.pollBuildStatus);
  const buildStatus = useBuildStore((s) => s.buildStatus);
  // 2026-06-27 — file-selector: point the preview at any .html in the tree.
  const previewFile = useBuildStore((s) => s.previewFile);
  const previewPath = useBuildStore((s) => s.previewPath);
  const currentProject = useProjectStore((s) => s.currentProject);
  const fileTree = useProjectStore((s) => s.fileTree);

  // Flatten the tree to the previewable .html files (source paths, sans the
  // leading slash). Built app (index.html → '') is offered as the first option.
  const htmlFiles = useMemo(() => {
    const out: string[] = [];
    const walk = (node: FileNode) => {
      if (node.type === 'file' && node.name.toLowerCase().endsWith('.html')) {
        out.push(node.path.replace(/^\/+/, ''));
      }
      node.children?.forEach(walk);
    };
    if (fileTree) walk(fileTree);
    // Stable, de-duped. Exclude: index.html (it IS the Built-app option) and
    // dist/ + public/ (Vite build output + static copies — duplicates of the
    // authored source demos; 'Built app' already covers dist/index.html).
    return Array.from(new Set(out))
      .filter((p) => p !== 'index.html')
      .filter((p) => !p.startsWith('dist/') && !p.startsWith('public/'))
      .sort();
  }, [fileTree]);

  // 2026-07-14 — .md files are ALSO selectable in the preview; picking one
  // renders formatted markdown (MarkdownPreviewPane) instead of the iframe.
  const mdFiles = useMemo(() => {
    const out: string[] = [];
    const walk = (node: FileNode) => {
      if (node.type === 'file' && node.name.toLowerCase().endsWith('.md')) {
        out.push(node.path.replace(/^\/+/, ''));
      }
      node.children?.forEach(walk);
    };
    if (fileTree) walk(fileTree);
    return Array.from(new Set(out))
      .filter((p) => !p.startsWith('dist/'))
      .sort();
  }, [fileTree]);
  // E.21.36 Workstream (b): public-URL bar reads from deployStore.
  const deploymentStatus = useDeployStore((s) => s.deploymentStatus);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  // Reviewer Q3 + Concern 3: only render the public-URL bar when:
  //   - URL is set (truthy), AND
  //   - status is terminal-success ('http-live' | 'https-live')
  // Pre-deploy state: status='not_deployed', url=null → bar hidden.
  // Mid-deploy state: status='syncing', url=set (backend writes url
  //   into active_deploys before bg task) → bar STILL HIDDEN until terminal.
  const showPublicUrlBar =
    !!deploymentStatus.url &&
    (deploymentStatus.status === 'http-live' ||
      deploymentStatus.status === 'https-live');

  const [deviceSize, setDeviceSize] = useState<DeviceSize>('desktop');
  // 2026-08-18: themeMode previously had ZERO consumers (the toggle was a
  // no-op). It now drives the markdown preview card; LIGHT is the default
  // per operator direction. The app-preview iframe never consumed it.
  const [themeMode, setThemeMode] = useState<ThemeMode>('light');
  const [scale, setScale] = useState(100);
  // 2026-07-08 — App | Browser toggle. 'app' = the built/previewed project
  // (iframe); 'browser' = a live view of the tenant's shared headless browser
  // (BrowserView), the same page the Pi/voice agents drive. Local (not persisted).
  const [paneMode, setPaneMode] = useState<'app' | 'browser'>('app');

  // 2026-07-08 — re-open control for a hidden editor. When the editor pane is
  // collapsed (via its header's Hide button) the preview fills the split and
  // the editor's own controls are gone, so the only re-open affordance lives
  // here (plus opening a file, which auto-expands). Shown only when collapsed.
  const editorCollapsed = useUIStore((s) => s.editorCollapsed);
  const setEditorCollapsed = useUIStore((s) => s.setEditorCollapsed);
  const infoCollapsed = useUIStore((s) => s.infoCollapsed);
  const setInfoCollapsed = useUIStore((s) => s.setInfoCollapsed);

  const handleRefresh = () => {
    if (iframeRef.current) {
      setPreviewLoading(true);
      // Reassigning .src to itself triggers an iframe reload — intentional pattern.
      // eslint-disable-next-line no-self-assign
      iframeRef.current.src = iframeRef.current.src;
    }
  };

  // INTERACT (2026-07-13; round-trip 2026-07-14) — Pi chat (ui_action) + voice
  // (spa_action module_action) drive a Blockworld-style voxel app in the preview
  // via the shared 'vibe:module-action' bus. Verb advertised from
  // moduleActions.ts under the core-view key 'view:designer'. The preview is
  // same-origin (/api/preview/*), so the iframe's own {type:'bw_cmd'} receiver
  // accepts this postMessage; apps that don't listen simply ignore it.
  //
  // Round-trip: blockworld_cmd carries an optional __reqId. The app replies
  // {type:'bw_cmd_result', id, lines} with the command's real output, which we
  // re-emit as a 'vibe:blockworld-result' CustomEvent so the ui_action tool can
  // return that output to the agent — its feedback loop (help/scenes/errors).
  useEffect(() => {
    const onModuleAction = (e: Event) => {
      const detail = (e as CustomEvent).detail as
        | { action?: string; params?: Record<string, unknown>; module_id?: string; __reqId?: string }
        | undefined;
      // No module_id gate: this verb exists ONLY here, so the verb IS the
      // address. Live-verified failure: the coder model correctly picked
      // blockworld_cmd but guessed module_id:'blockworld', and a strict
      // 'view:designer' filter silently dropped the dispatch.
      //
      // Pi arrives normalized as action:'blockworld_cmd' (uiActionTool). Voice
      // (voiceStore) dispatches the model's raw verb, and cyankiwi FABRICATES —
      // it sends the bare command as the action (e.g. action:'scenes'). Recover
      // both: blockworld_cmd uses params.cmd; anything else routes through
      // resolveBlockworldCmd, which returns a command only when the Blockworld
      // preview is the active context (else null → not ours).
      if (!detail) return;
      const cmd =
        detail.action === 'blockworld_cmd'
          ? typeof detail.params?.cmd === 'string'
            ? detail.params.cmd
            : undefined
          : (resolveBlockworldCmd(detail.action ?? '', detail.params ?? {}) ?? undefined);
      if (typeof cmd !== 'string' || !cmd.trim() || cmd.length > 2000) return;
      iframeRef.current?.contentWindow?.postMessage(
        { type: 'bw_cmd', cmd, id: detail.__reqId },
        window.location.origin,
      );
    };
    const onIframeMsg = (e: MessageEvent) => {
      if (e.origin !== window.location.origin) return;
      const d = e.data as
        | { type?: string; id?: string; lines?: unknown; name?: unknown; file?: unknown }
        | undefined;
      // ack = the Blockworld app is live (lets the driver keep waiting through a
      // long LLM build); result = the command's output.
      if (d?.type === 'bw_cmd_ack') {
        window.dispatchEvent(
          new CustomEvent('vibe:blockworld-result', { detail: { id: d.id, phase: 'ack' } }),
        );
        return;
      }
      // 2026-07-14 — Blockworld → SPA scene-save bridge. The agent can save a
      // large (compressed, ~600KB) scene AS a project file without pushing the
      // bytes through the LLM's write_file output ceiling: the Blockworld
      // `save_scene_to_project` command posts the file here and the SPA (which
      // holds the auth token) PUTs it to public/scenes/<name>.json. We ack back
      // so the app can report success/failure to the user.
      if (d?.type === 'bw_save_scene') {
        const reply = (ok: boolean, extra: Record<string, unknown>) =>
          iframeRef.current?.contentWindow?.postMessage(
            { type: 'bw_save_scene_result', id: d.id, ok, ...extra },
            window.location.origin,
          );
        const rawName = typeof d.name === 'string' ? d.name : '';
        const file = typeof d.file === 'string' ? d.file : '';
        // Basename only + safe charset (no path traversal); cap the size.
        const safe = (rawName.split('/').pop() ?? '')
          .replace(/\.json$/i, '')
          .replace(/[^a-zA-Z0-9_-]/g, '_')
          .replace(/^_+|_+$/g, '')
          .slice(0, 64);
        if (!safe) {
          reply(false, { error: 'invalid scene name' });
          return;
        }
        if (!file) {
          reply(false, { error: 'empty scene file' });
          return;
        }
        if (file.length > 25_000_000) {
          reply(false, { error: 'scene too large (>25MB)' });
          return;
        }
        const path = `public/scenes/${safe}.json`;
        void useProjectStore
          .getState()
          .writeFile(path, file)
          .then(() => reply(true, { path }))
          .catch((err) =>
            reply(false, { error: String(err instanceof Error ? err.message : err) }),
          );
        return;
      }
      if (d?.type !== 'bw_cmd_result') return;
      const lines = Array.isArray(d.lines)
        ? d.lines.filter((l): l is string => typeof l === 'string')
        : [];
      window.dispatchEvent(
        new CustomEvent('vibe:blockworld-result', { detail: { id: d.id, phase: 'result', lines } }),
      );
    };
    window.addEventListener('vibe:module-action', onModuleAction);
    window.addEventListener('message', onIframeMsg);
    return () => {
      window.removeEventListener('vibe:module-action', onModuleAction);
      window.removeEventListener('message', onIframeMsg);
    };
  }, []);

  // 2026-06-27: open the CURRENTLY-SELECTED preview target in a real browser
  // tab — full-size, top-level document (needed for things the cramped in-pane
  // iframe can't do, e.g. a pointer-lock game capturing the mouse). The URL is
  // computed from the selected file (previewPath) + project id so it always
  // reflects the dropdown and is NOT clobbered when a build resets previewUrl
  // to the built app. The /api/preview/* route is PUBLIC, so the new tab needs
  // no auth.
  const handleOpenExternal = () => {
    if (!currentProject) return;
    const clean = (previewPath ?? '').replace(/^\/+/, '');
    const url =
      clean && clean !== 'index.html'
        ? `/api/preview/${currentProject.id}/${clean}`
        : `/api/preview/${currentProject.id}/`;
    window.open(url, '_blank', 'noopener');
  };

  const handleIframeLoad = () => {
    setPreviewLoading(false);
  };

  const currentSize = deviceSizes[deviceSize];

  // 2026-07-14 — when the selected preview target is a .md file, render
  // markdown (MarkdownPreviewPane) rather than pointing the iframe at raw text.
  const isMdPreview = (previewPath ?? '').toLowerCase().endsWith('.md');

  // 2026-08-18: stable callback — an inline arrow here re-ran the pane's
  // fetch effect on EVERY parent re-render (visible as a content flash when
  // the theme toggle flips). setPreviewLoading from zustand is stable.
  const handleMdLoaded = useCallback(() => setPreviewLoading(false), [setPreviewLoading]);

  return (
    <div className="flex flex-col h-full w-full overflow-hidden">
      {/* E.21.36 Workstream (b) — public-URL bar above the iframe.
          Renders ONLY on terminal-success. The bar links to the
          actual deployed URL (HTTP after Phase A; HTTPS after Phase B
          daily cert cron flips the row). */}
      {showPublicUrlBar && (
        <div
          data-testid="public-deploy-url-bar"
          className="flex items-center justify-between px-3 py-1.5 bg-vibe-accent/10 border-b border-vibe-accent/30 text-xs"
        >
          <div className="flex items-center gap-2 min-w-0">
            <ExternalLink className="w-3.5 h-3.5 text-vibe-accent flex-shrink-0" />
            <span className="text-vibe-text-secondary flex-shrink-0">Deployed:</span>
            <a
              href={deploymentStatus.url ?? '#'}
              target="_blank"
              rel="noopener noreferrer"
              className="font-mono text-vibe-accent hover:underline truncate"
            >
              {deploymentStatus.url}
            </a>
          </div>
          {deploymentStatus.status === 'http-live' && (
            <span className="text-vibe-text-secondary italic flex-shrink-0 ml-2">
              HTTPS arrives within 24h (next 02:00 UTC cert cycle)
            </span>
          )}
        </div>
      )}

      {/* Toolbar — 2026-07-02: flex-wrap so the right control group wraps to a
          second row at narrow panel widths instead of being flex-crushed into
          unreadable slivers ("Open in Browser" squeezed to 16px against the
          chat panel's edge — user-reported). */}
      <div className="flex flex-wrap items-center justify-between gap-y-1 p-2 border-b border-vibe-border bg-vibe-bg-secondary">
        <div className="flex items-center gap-1">
          {/* App | Browser toggle (2026-07-08). 'Browser' shows the tenant's
              shared headless browser — the same page the Pi/voice agents drive. */}
          <div
            className="flex items-center rounded bg-vibe-bg-tertiary p-0.5 mr-1"
            role="group"
            aria-label="Preview mode"
          >
            <button
              type="button"
              onClick={() => setPaneMode('app')}
              data-testid="preview-mode-app"
              aria-pressed={paneMode === 'app'}
              title="App preview"
              className={clsx(
                'flex items-center gap-1 px-2 py-1 rounded text-xs',
                paneMode === 'app'
                  ? 'bg-vibe-accent text-white'
                  : 'text-vibe-text-secondary hover:text-vibe-text'
              )}
            >
              <AppWindow className="w-3.5 h-3.5" />
              App
            </button>
            <button
              type="button"
              onClick={() => setPaneMode('browser')}
              data-testid="preview-mode-browser"
              aria-pressed={paneMode === 'browser'}
              title="Live web browser (shared with the Pi / voice agents)"
              className={clsx(
                'flex items-center gap-1 px-2 py-1 rounded text-xs',
                paneMode === 'browser'
                  ? 'bg-vibe-accent text-white'
                  : 'text-vibe-text-secondary hover:text-vibe-text'
              )}
            >
              <Globe className="w-3.5 h-3.5" />
              Browser
            </button>
          </div>
          {/* Show-editor — only when the editor pane is hidden (2026-07-08) */}
          {editorCollapsed && (
            <button
              type="button"
              data-testid="preview-show-editor-button"
              onClick={() => setEditorCollapsed(false)}
              title="Show editor"
              aria-label="Show editor"
              className="flex items-center gap-1 p-1.5 mr-1 rounded text-vibe-text-secondary hover:bg-vibe-bg-tertiary hover:text-vibe-text"
            >
              <PanelLeftOpen className="w-4 h-4" />
              <span className="text-xs">Editor</span>
            </button>
          )}
          {/* Show-panel — reopen the bottom Info panel after its X/chevron
              closed it (2026-07-11). Mirrors Show-editor above. */}
          {infoCollapsed && (
            <button
              type="button"
              data-testid="preview-show-info-button"
              onClick={() => setInfoCollapsed(false)}
              title="Show panel (Problems / Output / Terminal / Git)"
              aria-label="Show panel"
              className="flex items-center gap-1 p-1.5 mr-1 rounded text-vibe-text-secondary hover:bg-vibe-bg-tertiary hover:text-vibe-text"
            >
              <PanelBottomOpen className="w-4 h-4" />
              <span className="text-xs">Panel</span>
            </button>
          )}
          {paneMode === 'app' && (
            <>
          {/* Device Buttons */}
          <button
            onClick={() => setDeviceSize('mobile')}
            className={clsx(
              'p-1.5 rounded',
              deviceSize === 'mobile'
                ? 'bg-vibe-accent text-white'
                : 'hover:bg-vibe-bg-tertiary text-vibe-text-secondary'
            )}
            title="Mobile (375x667)"
          >
            <Smartphone className="w-4 h-4" />
          </button>
          <button
            onClick={() => setDeviceSize('tablet')}
            className={clsx(
              'p-1.5 rounded',
              deviceSize === 'tablet'
                ? 'bg-vibe-accent text-white'
                : 'hover:bg-vibe-bg-tertiary text-vibe-text-secondary'
            )}
            title="Tablet (768x1024)"
          >
            <Tablet className="w-4 h-4" />
          </button>
          <button
            onClick={() => setDeviceSize('desktop')}
            className={clsx(
              'p-1.5 rounded',
              deviceSize === 'desktop'
                ? 'bg-vibe-accent text-white'
                : 'hover:bg-vibe-bg-tertiary text-vibe-text-secondary'
            )}
            title="Desktop (1280x800)"
          >
            <Monitor className="w-4 h-4" />
          </button>

          <div className="w-px h-4 bg-vibe-border mx-1" />

          {/* Theme Toggle */}
          <button
            onClick={() =>
              setThemeMode(themeMode === 'dark' ? 'light' : 'dark')
            }
            className="p-1.5 rounded hover:bg-vibe-bg-tertiary text-vibe-text-secondary"
            title="Toggle theme"
          >
            {themeMode === 'dark' ? (
              <Sun className="w-4 h-4" />
            ) : (
              <Moon className="w-4 h-4" />
            )}
          </button>

          <div className="w-px h-4 bg-vibe-border mx-1" />

          {/* 2026-06-27 — file selector: choose which file the preview renders.
              'Built app' = dist/index.html; any .html in the tree previews
              directly from source (no build). Mirrors the explorer: clicking
              a .html file in the tree also drives this (via previewFile). */}
          <select
            data-testid="preview-file-select"
            value={previewPath ?? ''}
            onChange={(e) => {
              if (currentProject) previewFile(currentProject.id, e.target.value);
            }}
            disabled={!currentProject}
            title="Choose which file to preview"
            className="max-w-[180px] bg-vibe-bg-tertiary text-vibe-text text-xs rounded px-2 py-1 border-none outline-none cursor-pointer disabled:opacity-50"
          >
            <option value="">Built app (index.html)</option>
            {htmlFiles.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
            {mdFiles.length > 0 && (
              <optgroup label="Markdown">
                {mdFiles.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </optgroup>
            )}
          </select>
            </>
          )}
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {paneMode === 'app' && (
            <>
          {/* Scale */}
          <select
            data-testid="preview-scale-select"
            value={scale}
            onChange={(e) => setScale(Number(e.target.value))}
            className="bg-vibe-bg-tertiary text-vibe-text text-xs rounded px-2 py-1 border-none outline-none cursor-pointer"
          >
            <option value={50}>50%</option>
            <option value={75}>75%</option>
            <option value={100}>100%</option>
            <option value={125}>125%</option>
          </select>

          {/* Rebuild — re-run the full project build (tsc + Vite) and refresh
              the preview. Available whenever a project is loaded, INCLUDING
              while a preview is already showing or a build is mid-flight
              (unlike the iframe-only Refresh, which is disabled during load).
              This is the toolbar-level rebuild affordance the empty-state
              button used to be the only home for. */}
          <button
            onClick={() => {
              if (!currentProject) return;
              // Same pair TopBar's Build button uses: POST /api/builds/{id}
              // then poll status. This actually recompiles (tsc + Vite).
              void startBuild(currentProject.id).then(() =>
                pollBuildStatus(currentProject.id)
              );
            }}
            disabled={!currentProject || buildStatus.status === 'building'}
            data-testid="preview-rebuild-button"
            className="flex items-center gap-1 px-2 py-1 rounded shrink-0 whitespace-nowrap hover:bg-vibe-bg-tertiary text-vibe-text-secondary text-xs disabled:opacity-50 disabled:cursor-not-allowed"
            title="Rebuild the project (compiles the whole app: tsc + Vite) and refresh the preview"
          >
            {buildStatus.status === 'building' ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <Hammer className="w-3.5 h-3.5" />
            )}
            {buildStatus.status === 'building' ? 'Building…' : 'Rebuild'}
          </button>

          {/* Refresh — reload the iframe only (no rebuild). */}
          <button
            onClick={handleRefresh}
            disabled={previewLoading || !previewUrl}
            className="p-1.5 rounded hover:bg-vibe-bg-tertiary text-vibe-text-secondary disabled:opacity-50"
            title="Reload the preview iframe (no rebuild)"
          >
            <RefreshCw
              className={clsx('w-4 h-4', previewLoading && 'animate-spin')}
            />
          </button>

          {/* Open the current preview in a real browser tab (full-size,
              top-level document). Prominent + labeled; enabled whenever a
              project is loaded (the /api/preview route is public). */}
          <button
            onClick={handleOpenExternal}
            disabled={!currentProject || (!previewUrl && !previewPath)}
            data-testid="preview-open-in-browser"
            className="flex items-center gap-1.5 px-2 py-1 rounded shrink-0 whitespace-nowrap bg-vibe-accent/10 hover:bg-vibe-accent/20 text-vibe-accent text-xs font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            title="Open the current preview in a new browser tab (full size)"
          >
            <ExternalLink className="w-3.5 h-3.5" />
            Open in Browser
          </button>
            </>
          )}
        </div>
      </div>

      {/* Preview Container — 1.99.7: items-start (was items-center). When
          the device preview (Desktop=800px tall) is taller than the
          visible container (often ~440px), `items-center` vertically-
          centers the wrapper which CLIPS THE TOP off-screen. Apps using
          `position: fixed; top: 0` (or anything anchored to viewport
          top) would render off-screen and the user sees pure white in
          the middle of the iframe. items-start anchors the top of the
          preview to the top of the container, so the user always sees
          their app's top — matching how a real browser renders. */}
      {paneMode === 'browser' ? (
        <div className="flex-1 min-h-0">
          <BrowserView />
        </div>
      ) : (
      <>
      <div className="flex-1 overflow-auto bg-vibe-bg flex items-start justify-center p-4">
        {isMdPreview && currentProject ? (
          <MarkdownPreviewPane
            projectId={currentProject.id}
            path={previewPath ?? ''}
            theme={themeMode}
            onLoaded={handleMdLoaded}
          />
        ) : previewUrl ? (
          <div
            data-testid="preview-frame-wrapper"
            className="relative bg-white rounded-lg shadow-2xl overflow-hidden transition-all duration-200"
            style={{
              width: currentSize.width,
              height: currentSize.height,
              // B1 (2026-05-06): use CSS `zoom` (layout-affecting) instead of
              // `transform: scale()` (preserves layout box). The outer
              // overflow-auto scroll container at line 219 measures pre-
              // transform geometry, so transform: scale() produced wrong
              // scrollbar math when scale != 100%. zoom resizes the layout
              // box, so scrollbars track correctly. See plan doc
              // 20260506_185500_PLAN_B1_B7_PREVIEW_VIEWPORT_AND_WIDGET_CSS.md.
              zoom: scale / 100,
            }}
          >
            {/* Loading overlay */}
            {previewLoading && (
              <div className="absolute inset-0 bg-vibe-bg/80 flex items-center justify-center z-10">
                <Loader2 className="w-8 h-8 text-vibe-accent animate-spin" />
              </div>
            )}

            {/* iframe */}
            <iframe
              ref={iframeRef}
              src={previewUrl}
              onLoad={handleIframeLoad}
              className="w-full h-full border-0"
              sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
              title="Preview"
            />
          </div>
        ) : buildStatus.status === 'building' ? (
          // E.21.31.2: show live build progress in the empty preview slot
          // so the user has immediate visual feedback. Sub-stage label
          // mirrors the TopBar pill — same stage source, same vocabulary.
          <div
            className="text-center text-vibe-text-secondary"
            data-testid="preview-building-state"
          >
            <Loader2 className="w-16 h-16 mx-auto mb-4 text-vibe-accent animate-spin" />
            <div className="text-lg">{buildStageMessage(buildStatus.stage)}</div>
            <div className="text-sm mt-2">
              Compiling the whole project (type-check + Vite bundle), not just one
              file.
            </div>
            <div className="text-sm mt-1">
              See <span className="font-mono">Output</span> tab below for live logs
            </div>
          </div>
        ) : (
          <div className="text-center text-vibe-text-secondary">
            <Monitor className="w-16 h-16 mx-auto mb-4 opacity-50" />
            <div className="text-lg">No preview available</div>
            <div className="text-sm mt-2">
              Build the project to see a live preview
            </div>
            <button
              data-testid="start-preview-button"
              onClick={() => void previewProject(currentProject?.id)}
              disabled={!currentProject}
              className="mt-4 px-4 py-2 bg-vibe-accent hover:bg-vibe-accent-hover disabled:opacity-50 disabled:cursor-not-allowed text-white rounded text-sm"
            >
              Refresh Preview
            </button>
          </div>
        )}
      </div>

      {/* Device Info + build-status summary. The store already tracks
          duration/errors/warnings/healed after each build (buildStore); surface
          them here so the user gets concrete outcome detail, not just a stage. */}
      <div className="px-3 py-1.5 border-t border-vibe-border bg-vibe-bg-secondary text-xs text-vibe-text-secondary flex items-center justify-between gap-2">
        <span className="shrink-0">
          {currentSize.width} × {currentSize.height}
        </span>
        <span
          data-testid="build-status-summary"
          className="min-w-0 truncate text-center flex-1"
        >
          {buildStatus.status === 'success' &&
          buildStatus.buildDurationMs != null ? (
            <span className="inline-flex items-center gap-1 justify-center">
              <CheckCircle2 className="w-3.5 h-3.5 text-green-500 shrink-0" />
              Built in {(buildStatus.buildDurationMs / 1000).toFixed(1)}s ·{' '}
              {buildStatus.errors?.length ?? 0} error
              {(buildStatus.errors?.length ?? 0) === 1 ? '' : 's'}
              {(buildStatus.warnings?.length ?? 0) > 0 &&
                ` · ${buildStatus.warnings?.length} warning${
                  buildStatus.warnings?.length === 1 ? '' : 's'
                }`}
              {buildStatus.healed && ' · recovered automatically'}
            </span>
          ) : buildStatus.status === 'error' ? (
            <span className="inline-flex items-center gap-1 justify-center text-red-400">
              <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
              Build failed
              {(buildStatus.errors?.length ?? 0) > 0 &&
                ` · ${buildStatus.errors?.length} error${
                  buildStatus.errors?.length === 1 ? '' : 's'
                }`}{' '}
              — see Output
            </span>
          ) : null}
        </span>
        <span className="capitalize shrink-0">{deviceSize}</span>
      </div>
      </>
      )}
    </div>
  );
}
